import { useContactOptedOut } from 'app/hooks/useContactOptedOut'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useScheduledSend } from 'app/hooks/useScheduledSend'
import { useSmsQuietHours } from 'app/hooks/useSmsQuietHours'
import { useThreadAgentHandling } from 'app/hooks/useThreadAgentHandling'

export type SmsSchedulingTarget = {
  /** The recipient. Absent means no recipient is chosen yet, which withholds the control. */
  contactId?: string | null
  /** The number the message would go to, which is what opt-out is keyed on. */
  phone?: string | null
  /** The sending location, which owns both the practice clock and the opt-out records. */
  locationId?: string | null
}

/**
 * Everything a single-recipient composer needs to offer a scheduled send.
 *
 * Three surfaces assemble this the same way: the practice timezone, the
 * recipient's opt-out state, and both of those fed into `useScheduledSend`. It
 * is one hook rather than three call sites because the middle step is the
 * compliance gate. Two copies of it that drift means one composer offering to
 * text somebody who replied STOP, and the drift would be invisible: the
 * dispatcher refuses the send at fire time, so the only symptom is a control
 * that should never have existed.
 *
 * Quiet hours come back untouched (`quiet`, `resolved`) rather than folded into
 * a verdict. Whether they block a given composer depends on who spoke last in
 * that thread, which is the surface's own question and not this hook's.
 */
export function useSmsScheduling({ contactId, phone, locationId }: SmsSchedulingTarget) {
  const location = locationId ?? undefined
  const scheduledSmsEnabled = useFeatureToggle(`scheduled-sms`)
  const { quiet, timezone, resolved } = useSmsQuietHours(location)
  const optedOut = useContactOptedOut(phone, location)
  const agentHandling = useThreadAgentHandling(location, contactId, scheduledSmsEnabled)
  const scheduled = useScheduledSend({
    contactIds: contactId ? [contactId] : [],
    locationId: location,
    timezone,
    optedOut,
    agentHandling,
  })
  return { scheduled, timezone, quiet, resolved, optedOut, agentHandling }
}
